import { useMemo } from 'react'
import type { ErrorLike } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useQuery } from '@apollo/client/react'
import { useContentfulExperiment } from '@/experimentation'
import { GetSalesQuery } from '@/types/codegen-contentful'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { getContentfulClient } from '../ApolloClient'
import { useGuildMember } from '../GuildUserService'
import { GET_SALES_QUERY } from './queries'
import { getActiveSales } from './shared'
import { ValidSale, ValidSaleCategory } from './types'

interface UseSaleBannerResult {
  error?: ErrorLike
  loading: boolean
  saleWithBanner: ValidSale | null
}

const ALL_SALES_DISABLED_PATH_REGEXES = [/\/m\/.*/]
const GUILD_SALE_DISABLED_PATH_REGEXES = [/\/tickets.*/]
const GUILD_MEMBERS_ONLY_SALE_DISABLED_PATH_REGEXES: RegExp[] = []

export function useSaleBanner(): UseSaleBannerResult {
  const { locale } = useLocale()
  const router = useSafeRouter()
  const { isGuildMember } = useGuildMember()

  // Use preview mode only in non-production environments to reduce load and enable caching.
  // Draft/unpublished Sale objects won't appear in production, which is correct behavior.
  // This allows the proxy's 30s cache to work, significantly reducing Contentful API load.
  const preview = false
  const opts = { locale, preview }
  const queryResult = useQuery<GetSalesQuery>(GET_SALES_QUERY, {
    variables: opts,
    client: getContentfulClient(opts),
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
    context: {
      fetchOptions: {
        timeout: 10000,
      },
    },
  })

  const saleResult = useMemo(() => {
    const { data, loading, error } = queryResult

    if (error) {
      // Enhanced logging to diagnose "Load failed" errors
      const errorDetails = {
        locale,
        preview,
        path: router.asPath,
        errorMessage: error.message,
        errorName: error.name,
        // Check if we got partial data despite the error
        hasPartialData: !!data,
        dataItemCount: data?.saleCollection?.items?.length ?? 0,
        // GraphQL-specific errors
        ...(CombinedGraphQLErrors.is(error)
          ? {
              graphQLErrors: error.errors.map((e) => ({
                message: e.message,
                path: e.path,
                extensions: e.extensions,
              })),
            }
          : {}),
        // Client configuration context
        clientConfig: {
          errorPolicy: 'all',
          fetchPolicy: 'cache-first',
          timeout: 10000,
        },
        // Browser/environment context
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        isOnline: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        connectionType:
          typeof navigator !== 'undefined' && 'connection' in navigator
            ? // @ts-expect-error - connection is not in standard navigator type
              navigator.connection?.effectiveType
            : undefined,
      }

      logger().error('An error occurred while attempting to query Sale objects from Contentful!', errorDetails, error)

      return { loading, error, saleWithBanner: null }
    }

    if (loading) {
      return { loading, error, saleWithBanner: null }
    }

    if (!data) {
      logger().warn('Sale query completed but returned no data', {
        locale,
        preview,
        path: router.asPath,
      })
      return { loading, error, saleWithBanner: null }
    }

    try {
      const saleWithBanner = getActiveSaleForUser(data, isGuildMember, router.asPath)
      return { loading, error, saleWithBanner }
    } catch (err) {
      logger().error(
        'Error processing active sales',
        {
          locale,
          preview,
          path: router.asPath,
        },
        err instanceof Error ? err : new Error(String(err)),
      )
      return { loading, error, saleWithBanner: null }
    }
  }, [isGuildMember, queryResult, router.asPath, locale, preview])

  // Run sale copy experiment to potentially override pitch/abbreviatedPitch
  const experimentItems = useMemo(
    () => saleResult.saleWithBanner?.saleCopyExperimentCollection?.items?.filter((item) => item !== null) ?? [],
    [saleResult.saleWithBanner?.saleCopyExperimentCollection?.items],
  )

  const saleCopyExperiment = useContentfulExperiment(experimentItems)

  const result = useMemo<UseSaleBannerResult>(() => {
    if (!saleResult.saleWithBanner || !saleCopyExperiment) {
      return saleResult
    }

    // Override pitch, abbreviatedPitch, and saleBannerText with experiment values if available
    // Use || instead of ?? to also fallback when values are empty strings
    return {
      ...saleResult,
      saleWithBanner: {
        ...saleResult.saleWithBanner,
        pitch: saleCopyExperiment.pitch || saleResult.saleWithBanner.pitch,
        abbreviatedPitch: saleCopyExperiment.abbreviatedPitch || saleResult.saleWithBanner.abbreviatedPitch,
        saleBannerText: saleCopyExperiment.saleBannerText || saleResult.saleWithBanner.saleBannerText,
      },
    }
  }, [saleResult, saleCopyExperiment])

  return result
}

export function getActiveSaleForUser(
  data: GetSalesQuery,
  isGuildMember: boolean,
  currentPath: string,
): ValidSale | null {
  const activeSales = getActiveSales(data)
  const salesWithBanner = activeSales.filter((s) => s.showSaleBanner)

  const salesForCurrentPath = salesWithBanner.filter((s) => {
    const isCategoryDisabledForPath = (category: ValidSaleCategory, disabledRegexes: RegExp[]): boolean => {
      return s.category === category && disabledRegexes.some((r) => r.test(currentPath))
    }

    if (ALL_SALES_DISABLED_PATH_REGEXES.some((r) => r.test(currentPath))) return false
    if (isCategoryDisabledForPath('guild', GUILD_SALE_DISABLED_PATH_REGEXES)) return false
    if (isCategoryDisabledForPath('guild-members-only', GUILD_MEMBERS_ONLY_SALE_DISABLED_PATH_REGEXES)) return false

    return true
  })

  const salesForThisUser = salesForCurrentPath.filter((s) => {
    if (isGuildMember) {
      return s.category === 'guild-members-only'
    } else {
      return s.category === 'guild'
    }
  })

  const saleWithBanner = salesForThisUser[0] || null

  if (salesForThisUser.length === 0) {
    logger().debug('There are currently no active sales with a banner that apply to this user.')
    return null
  } else if (salesForThisUser.length === 1) {
    logger().debug('Successfully fetched an active sale with a banner that applies to this user.', { saleWithBanner })
    return saleWithBanner
  } else {
    // Might need some fancy logic here someday, but for now concurrent sales should be an edge case, so just return the first.
    logger().error(
      'There are currently multiple active sales with a banner that apply to this user! Only one will be used.',
      {
        saleWithBanner,
        salesForThisUser,
      },
    )
    return saleWithBanner
  }
}
